import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, FileText, FileSpreadsheet, Lock, Crown, File, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const Templates = () => {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadTemplates();
    if (user) loadUsage();
  }, [user]); // eslint-disable-line

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const response = await axios.get(`${API}/templates/my-usage`);
      setUsage(response.data);
    } catch (error) {
      // user might not be logged in
    }
  };

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  const categories = ["Contabilidad", "Tributación", "Laboral", "Finanzas"];
  const isAnnualSubscriber = usage?.is_annual_subscriber === true;
  const remaining = usage?.remaining ?? 0;
  const limit = usage?.limit ?? 5;
  const used = usage?.used ?? 0;
  const downloadedIds = usage?.downloaded_template_ids ?? [];

  const getIcon = (type) => {
    switch (type) {
      case "excel": return FileSpreadsheet;
      case "pdf": return FileText;
      default: return File;
    }
  };

  const handleDownload = async (template) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setDownloadingId(template.id);
    try {
      const res = await axios.post(`${API}/templates/${template.id}/download`);
      window.open(res.data.url, "_blank");
      if (res.data.consumed_quota === false) {
        toast.success("Descarga iniciada. Ya la habías descargado este mes, no consume cupo.");
      } else {
        toast.success(`Descarga iniciada. Has usado ${res.data.used} de ${res.data.limit} descargas este mes.`);
      }
      loadUsage();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Error al descargar";
      if (status === 403) {
        toast.error("Esta función es exclusiva del Plan PRO Anual.");
      } else if (status === 429) {
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <FileText className="w-10 h-10 text-[#E50914]" />
            <h1 className="text-4xl md:text-5xl font-black text-white">
              Plantillas <span className="text-[#E50914]">Descargables</span>
            </h1>
          </div>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Plantillas profesionales de contabilidad, tributación, laboral y finanzas. Exclusivas para suscriptores del <span className="text-[#E50914] font-semibold">Plan PRO Anual</span>.
          </p>

          {/* Usage banner */}
          {user && isAnnualSubscriber && (
            <div
              className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 max-w-2xl mx-auto"
              data-testid="usage-banner"
            >
              <p className="text-emerald-300 font-semibold">
                ✨ Plan PRO Anual activo
              </p>
              <p className="text-emerald-200/80 text-sm mt-1" data-testid="usage-counter-text">
                <span className="font-bold">{used} de {limit} descargas utilizadas este mes</span> ({remaining} disponibles)
              </p>
              <div className="mt-2 h-2 bg-black/40 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-[#E50914] transition-all"
                  style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
                />
              </div>
              <p className="text-emerald-200/60 text-xs mt-2">
                Máximo {limit} plantillas diferentes por mes. Volver a descargar una plantilla ya descargada este mes no consume cupo. El cupo se renueva el día 1 de cada mes y no se acumula.
              </p>
              {remaining === 0 && (
                <p className="text-amber-300 text-sm mt-2 font-semibold" data-testid="limit-reached-msg">
                  Has agotado tu cupo de este mes. Podrás descargar nuevas plantillas cuando se renueve el próximo mes.
                </p>
              )}
            </div>
          )}

          {user && usage && !isAnnualSubscriber && (
            <div
              className="mt-6 bg-[#E50914]/10 border border-[#E50914]/30 rounded-lg p-5 max-w-2xl mx-auto"
              data-testid="non-annual-banner"
            >
              <Crown className="w-10 h-10 text-[#E50914] mx-auto mb-2" />
              <p className="text-white font-bold text-lg mb-1">Exclusivo Plan PRO Anual</p>
              <p className="text-white/70 text-sm mb-4">
                Las plantillas descargables solo están disponibles para suscriptores del Plan PRO Anual ($100/año).
                {usage.plan === "monthly" && " Tu plan actual es Mensual."}
              </p>
              <Button
                onClick={() => navigate("/contaflix-pro")}
                className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                data-testid="upgrade-to-annual-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Cambiar a Plan Anual
              </Button>
            </div>
          )}

          {!user && (
            <div className="mt-6 bg-white/5 border border-white/20 rounded-lg p-5 max-w-2xl mx-auto">
              <Lock className="w-10 h-10 text-white/60 mx-auto mb-2" />
              <p className="text-white font-semibold mb-2">Inicia sesión para descargar</p>
              <Button
                onClick={() => navigate("/auth")}
                className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
              >
                Iniciar sesión
              </Button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-[#E50914] animate-spin" />
            <span className="ml-2 text-white/60">Cargando plantillas...</span>
          </div>
        )}

        {!loading && templates.length === 0 && (
          <div className="text-center py-20 bg-white/5 rounded-lg border border-white/10">
            <FileSpreadsheet className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No hay plantillas disponibles</h3>
            <p className="text-white/60">Las plantillas se agregarán próximamente</p>
          </div>
        )}

        {!loading && categories.map((category) => {
          const categoryTemplates = templatesByCategory[category] || [];
          if (categoryTemplates.length === 0) return null;

          return (
            <div key={category} className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">{category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {categoryTemplates.map((template) => {
                  const IconComponent = getIcon(template.type);
                  const alreadyDownloaded = downloadedIds.includes(template.id);
                  const canDownload = isAnnualSubscriber && (remaining > 0 || alreadyDownloaded);
                  const limitReached = isAnnualSubscriber && remaining === 0 && !alreadyDownloaded;

                  return (
                    <Card
                      key={template.id}
                      className="bg-card/50 backdrop-blur-md border-white/10 hover:border-[#E50914]/50 transition-all relative overflow-hidden flex flex-col"
                      data-testid={`template-card-${template.id}`}
                    >
                      {!isAnnualSubscriber && (
                        <div className="absolute top-2 right-2 z-10">
                          <Badge className="bg-[#E50914] text-white border-0">
                            <Lock className="w-3 h-3 mr-1" />
                            ANUAL
                          </Badge>
                        </div>
                      )}
                      {isAnnualSubscriber && alreadyDownloaded && (
                        <div className="absolute top-2 right-2 z-10">
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-0" data-testid={`already-downloaded-${template.id}`}>
                            Ya descargada
                          </Badge>
                        </div>
                      )}
                      <CardHeader className="flex-1">
                        <IconComponent className="w-10 h-10 text-[#E50914] mb-2" />
                        <CardTitle className="text-white text-lg">{template.title}</CardTitle>
                        <CardDescription className="text-white/60 text-sm">
                          {template.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button
                          onClick={() => {
                            if (!user) return navigate("/auth");
                            if (!isAnnualSubscriber) return navigate("/contaflix-pro");
                            handleDownload(template);
                          }}
                          disabled={downloadingId === template.id || limitReached}
                          className={
                            canDownload
                              ? "w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                              : limitReached
                              ? "w-full bg-amber-500/20 text-amber-200 cursor-not-allowed"
                              : "w-full bg-white/5 text-white/40"
                          }
                          data-testid={`download-template-${template.id}`}
                        >
                          {downloadingId === template.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                          ) : limitReached ? (
                            <><AlertCircle className="w-4 h-4 mr-2" />Límite alcanzado</>
                          ) : canDownload ? (
                            <><Download className="w-4 h-4 mr-2" />Descargar</>
                          ) : (
                            <><Lock className="w-4 h-4 mr-2" />Requiere Plan Anual</>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* CTA Section: only for users not annual */}
        {!loading && templates.length > 0 && user && !isAnnualSubscriber && (
          <div className="mt-16 text-center bg-gradient-to-r from-[#E50914]/10 to-transparent border border-[#E50914]/20 rounded-lg p-12">
            <Crown className="w-16 h-16 text-[#E50914] mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Desbloquea todas las plantillas
            </h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Con el Plan PRO Anual ($100/año) descargas hasta {limit} plantillas diferentes por mes y obtienes acceso completo a todos los cursos.
            </p>
            <Button
              onClick={() => navigate("/contaflix-pro")}
              size="lg"
              className="bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold px-8 py-6 text-lg"
            >
              Suscribirme al Plan Anual
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Templates;
